import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { apiFetch, v1Path } from '../../lib/api';
import { orderedSectionKeys, serviceRequestFieldLabel, serviceRequestHandoffs, typeDataOf, type ServiceRequestType } from '../../lib/service-requests';
import type { ProjectDto } from '../../lib/work-types';
import { useOverlays } from './overlay-context';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

export type ExtraDepartmentStep = {
  id: string;
  title: string;
  project_id: string;
  assignee_id: string | null;
  due_date: string | null;
  work_item_id: string | null;
};

const MANUAL_SECTION: Record<string, string> = {
  non_standard_requirements: 'No automatic task. Add a step below if legal, software, or tech needs one.',
  supporting_information: 'No task. Upload the files under Documents.',
  sales_confirmation: 'A sign-off on this request, not a department task.',
};

type Row = {
  key: string;
  projectId: string | null;
  title: string;
  detail: string | null;
  waiting: boolean;
  extra: ExtraDepartmentStep | null;
};

function extraStepsOf(value: unknown): ExtraDepartmentStep[] {
  if (!Array.isArray(value)) return [];
  return value.filter((row): row is ExtraDepartmentStep => Boolean(row) && typeof row === 'object' && typeof (row as ExtraDepartmentStep).id === 'string');
}

export function extraStepsFromDetails(details: Record<string, unknown> | null | undefined): ExtraDepartmentStep[] {
  return extraStepsOf(details?.extra_steps);
}

/**
 * The department tasks this request will create: routed sections, fixed templates such as
 * packaging, and steps added on this request. A section with a condition stays visible
 * but marked waiting until the answer matches.
 */
export function departmentStepRows(type: ServiceRequestType, typeData: Record<string, unknown>, extra: ExtraDepartmentStep[]): Row[] {
  const schema = type.field_schema ?? {};
  const routes = schema.section_routes ?? {};
  const rows: Row[] = [];
  for (const handoff of serviceRequestHandoffs(type)) {
    for (const template of handoff.task_templates ?? []) {
      const title = template.title?.trim();
      if (!title) continue;
      rows.push({ key: `template:${handoff.linked_project_id}:${title}`, projectId: handoff.linked_project_id, title, detail: null, waiting: false, extra: null });
    }
  }
  for (const section of orderedSectionKeys(schema)) {
    const route = routes[section];
    const label = schema.section_labels?.[section] ?? serviceRequestFieldLabel(section);
    if (!route) {
      const note = MANUAL_SECTION[section];
      if (note) rows.push({ key: `manual:${section}`, projectId: null, title: label, detail: note, waiting: false, extra: null });
      continue;
    }
    const when = route.when;
    const waiting = Boolean(when && typeData[when.field] !== when.equals);
    const detail = [
      route.note,
      waiting && when ? `Created when ${serviceRequestFieldLabel(when.field, type)} is ${when.equals}.` : null,
    ].filter(Boolean).join(' ');
    for (const projectId of route.project_ids) {
      rows.push({
        key: `section:${section}:${projectId}`,
        projectId,
        title: label,
        detail: detail || null,
        waiting,
        extra: null,
      });
    }
  }
  for (const step of extra) {
    rows.push({
      key: `extra:${step.id}`,
      projectId: step.project_id,
      title: step.title,
      detail: step.work_item_id ? 'Already in the stream' : null,
      waiting: false,
      extra: step,
    });
  }
  return rows;
}

export default function DepartmentSteps({
  requestType,
  details,
  readOnly,
  saving,
  onSave,
}: {
  requestType: ServiceRequestType;
  details: Record<string, unknown>;
  readOnly: boolean;
  saving: boolean;
  onSave: (steps: ExtraDepartmentStep[]) => Promise<void>;
}) {
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const { openCreateWorkItem } = useOverlays();
  const steps = extraStepsFromDetails(details);
  const typeData = typeDataOf(details);
  const rows = departmentStepRows(requestType, typeData, steps);
  const nameOf = (id: string | null) => projects.find((project) => project.id === id)?.name ?? 'Department';

  useEffect(() => {
    let cancelled = false;
    void apiFetch<{ items: ProjectDto[] }>(v1Path('/projects?limit=100&offset=0'))
      .then((result) => {
        if (cancelled) return;
        setProjects((result.items ?? []).filter((project) => !project.archived_at && project.identifier !== 'SR'));
      })
      .catch(() => { if (!cancelled) setProjects([]); });
    return () => { cancelled = true; };
  }, []);

  function addStep() {
    openCreateWorkItem({
      capture: async (draft) => {
        const step: ExtraDepartmentStep = {
          id: crypto.randomUUID(),
          title: draft.title,
          project_id: draft.projectId,
          assignee_id: draft.ownerIds[0] ?? null,
          due_date: draft.dueDate || null,
          work_item_id: null,
        };
        await onSave([...steps, step]);
      },
    });
  }

  const grouped: { key: string; rows: Row[] }[] = [];
  for (const row of rows) {
    const key = row.projectId ?? 'none';
    const group = grouped.find((entry) => entry.key === key);
    if (group) group.rows.push(row);
    else grouped.push({ key, rows: [row] });
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Department steps</CardTitle>
        <CardDescription>Pushing this request creates a task for each step. Sections with no department stay on the request.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {grouped.map((group) => (
          <div key={group.key} className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">{group.key === 'none' ? 'On this request' : nameOf(group.key)}</p>
            <ul className="space-y-1">
              {group.rows.map((row) => (
                <li key={row.key} className={cn('flex items-start justify-between gap-3 rounded-lg border px-3 py-2', row.waiting && 'border-dashed opacity-70')}>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{row.title}</span>
                    {row.detail ? <span className="block text-xs text-muted-foreground">{row.detail}</span> : null}
                  </span>
                  {row.extra && !row.extra.work_item_id && !readOnly ? (
                    <Button type="button" variant="ghost" size="sm" disabled={saving} aria-label={`Remove ${row.title}`} onClick={() => void onSave(steps.filter((step) => step.id !== row.extra?.id))}>
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ))}
        {readOnly ? null : (
          <div className="border-t pt-3">
            <Button type="button" size="sm" variant="outline" disabled={saving} onClick={addStep}>
              <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
              Add step
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
